import React, { useEffect, useId, useState } from 'react';

import { Alert, Box, Button, Dialog, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography } from '@mui/material';

import { CopyToClipboardButton, DialogHeaderActions } from '@@components';

import { EmbedDeploymentModel } from '../../../api/api';
import { getEmbedViewName, useEmbedApp } from './useEmbedApp';

export const defaultEmbedEndpoints = {
	runtimeApiUrl: 'https://service.randol.io/runtime-api',
	scriptUrl: 'https://app.randol.io/embedded/embed.js',
};
const getEmbedEndpoints = (deployment: EmbedDeploymentModel) => ({
	runtimeApiUrl: deployment.runtimeApiUrl?.trim() || defaultEmbedEndpoints.runtimeApiUrl,
	scriptUrl: deployment.scriptUrl?.trim() || defaultEmbedEndpoints.scriptUrl,
});

const escapeAttribute = (value: string) => value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const escapeInputAttribute = (value: string) => value.replace(/&/g, '&amp;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
function parseExampleInput(json: string): Record<string, string> {
	const value = JSON.parse(json);
	if (
		!value ||
		typeof value !== 'object' ||
		Array.isArray(value) ||
		Object.values(value).some((item) => typeof item !== 'string') ||
		Object.keys(value).some((key) => ['__proto__', 'constructor', 'prototype'].includes(key))
	) {
		throw new Error('Enter a JSON object with string values and no reserved keys.');
	}
	return value;
}
export function buildEmbedSnippet(deployment: EmbedDeploymentModel, viewId: string, input: Record<string, string> = {}) {
	if (!deployment.publishableKey || !deployment.allowedViewIds.includes(viewId)) {
		return '';
	}
	const endpoints = getEmbedEndpoints(deployment);
	return (
		'<script src="' +
		escapeAttribute(endpoints.scriptUrl) +
		'" defer></script>\n<randol-view\n  embed-key="' +
		escapeAttribute(deployment.publishableKey) +
		'"\n  api-url="' +
		escapeAttribute(endpoints.runtimeApiUrl) +
		'"\n  view-id="' +
		escapeAttribute(viewId) +
		'"\n' +
		(Object.keys(input).length ? "  input='" + escapeInputAttribute(JSON.stringify(input)) + "'\n" : '') +
		'  style="display:block;min-height:400px">\n' +
		'  <div role="status" aria-live="polite" style="min-height:220px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;padding:32px 24px;text-align:center;font:inherit;color:inherit;box-sizing:border-box">\n' +
		'    <progress aria-label="Loading view" style="width:160px;max-width:100%;accent-color:currentColor"></progress>\n' +
		'    <strong>Loading your experience…</strong>\n' +
		'    <span>Please wait a moment. If this takes too long, refresh this page.</span>\n' +
		'  </div>\n</randol-view>'
	);
}
export default function EmbedSnippet({ deployment, changeSetId }: { deployment: EmbedDeploymentModel; changeSetId?: string }) {
	const previewTitleId = useId();
	const [isMaximized, setIsMaximized] = useState(false);
	const app = useEmbedApp(deployment.appDefinitionId, changeSetId);
	const [viewId, setViewId] = useState(deployment.defaultViewId);
	const [preview, setPreview] = useState(false);
	const [error, setError] = useState('');
	const [inputText, setInputText] = useState('{}');
	const [input, setInput] = useState<Record<string, string>>({});
	const [inputError, setInputError] = useState('');
	const inputSettingsKey = `randol-embed-example:${deployment.id}:${viewId}`;
	useEffect(() => {
		let saved = '{}';
		try {
			saved = sessionStorage.getItem(inputSettingsKey) ?? '{}';
		} catch {
			/* Browser storage is optional. */
		}
		setInputText(saved);
		try {
			setInput(parseExampleInput(saved));
		} catch {
			setInput({});
		}
		setInputError('');
	}, [inputSettingsKey]);
	const inputEditor = (
		<Stack spacing={1}>
			<TextField
				label="Example view input (JSON)"
				multiline
				minRows={3}
				value={inputText}
				onChange={(event) => setInputText(event.target.value)}
				error={!!inputError}
				helperText={inputError || 'String values passed to the View $input. Remembered in this browser tab; included in the snippet. Do not include secrets.'}
			/>
			<Button
				onClick={() => {
					try {
						const value = parseExampleInput(inputText);
						setInput(value);
						setInputError('');
						try {
							sessionStorage.setItem(inputSettingsKey, JSON.stringify(value));
						} catch {
							/* Browser storage is optional. */
						}
					} catch (failure) {
						setInputError(failure instanceof Error ? failure.message : 'Invalid input.');
					}
				}}
			>
				Apply inputs{preview ? ' to preview' : ''}
			</Button>
		</Stack>
	);
	useEffect(() => {
		setViewId(deployment.defaultViewId);
		setPreview(false);
	}, [deployment.defaultViewId, deployment.publishableKey, deployment.runtimeApiUrl, deployment.scriptUrl]);
	const endpoints = getEmbedEndpoints(deployment);
	const snippet = buildEmbedSnippet(deployment, viewId, input);
	const canPreview = deployment.isEnabled && deployment.allowedOrigins.includes(window.location.origin) && !!snippet;
	const showPreview = async () => {
		setError('');
		try {
			const url = new URL(endpoints.scriptUrl);
			if (url.protocol !== 'https:' && !(url.protocol === 'http:' && ['localhost', '127.0.0.1'].includes(url.hostname))) {
				throw new Error('The SDK URL must use HTTPS or localhost.');
			}
			const existing = document.querySelector<HTMLScriptElement>('script[data-randol-embed]');
			if (existing && existing.src !== url.href) {
				throw new Error('Reload the portal before previewing a different SDK URL.');
			}
			if (!existing) {
				await new Promise<void>((resolve, reject) => {
					const script = document.createElement('script');
					script.src = url.href;
					script.dataset.randolEmbed = 'true';
					script.onload = () => resolve();
					script.onerror = () => {
						script.remove();
						reject(new Error('The SDK could not be loaded.'));
					};
					document.head.append(script);
				});
			}
			setPreview(true);
		} catch (failure) {
			setError(failure instanceof Error ? failure.message : 'Preview failed.');
		}
	};
	return (
		<Stack spacing={2}>
			<Alert severity="info">The publishable embed key is visible to website visitors.</Alert>
			<Stack direction="row" alignItems="center">
				<TextField fullWidth label="Publishable embed key" value={deployment.publishableKey} InputProps={{ readOnly: true }} />
				<CopyToClipboardButton value={deployment.publishableKey} />
			</Stack>
			<TextField label="Runtime API URL" value={endpoints.runtimeApiUrl} InputProps={{ readOnly: true }} />
			<TextField label="Embed script URL" value={endpoints.scriptUrl} InputProps={{ readOnly: true }} />
			{!snippet && <Alert severity="warning">An applied App Embed key and an allowed initial view are required to generate the snippet.</Alert>}
			<TextField
				select
				label="Example initial view"
				value={viewId}
				onChange={(e) => {
					setViewId(e.target.value);
					setPreview(false);
				}}
			>
				{deployment.allowedViewIds.map((id) => (
					<MenuItem key={id} value={id}>
						{getEmbedViewName(
							app.data?.views.find((view) => view.Id === id),
							id,
						)}
					</MenuItem>
				))}
			</TextField>
			{!preview && inputEditor}
			{snippet && (
				<Box sx={{ position: 'relative' }}>
					<TextField fullWidth label="HTML snippet" multiline minRows={7} value={snippet} InputProps={{ readOnly: true, sx: { pr: 6 } }} />
					<CopyToClipboardButton value={snippet} title="Copy HTML snippet" edge={false} sx={{ position: 'absolute', top: 8, right: 8 }} />
				</Box>
			)}
			{!deployment.allowedOrigins.includes(window.location.origin) && (
				<Typography variant="body2">To preview here, allow {window.location.origin} in this environment.</Typography>
			)}
			<Button disabled={!canPreview} onClick={() => void showPreview()}>
				Preview
			</Button>
			{error && <Alert severity="error">{error}</Alert>}
			{preview && canPreview && (
				<Dialog
					open
					onClose={() => setPreview(false)}
					fullScreen={isMaximized}
					fullWidth
					maxWidth="lg"
					aria-labelledby={previewTitleId}
					PaperProps={{ sx: { height: isMaximized ? '100%' : '80vh' } }}
				>
					<DialogTitle id={previewTitleId} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<Typography component="span" variant="h6" sx={{ flex: 1, minWidth: 0 }}>
							App Embed preview —{' '}
							{getEmbedViewName(
								app.data?.views.find((view) => view.Id === viewId),
								viewId,
							)}
						</Typography>
						<DialogHeaderActions isMaximized={isMaximized} onToggleMaximized={() => setIsMaximized((value) => !value)} onClose={() => setPreview(false)} />
					</DialogTitle>
					<DialogContent dividers sx={{ flex: 1, overflowY: 'auto' }}>
						{inputEditor}
						{React.createElement('randol-view', {
							'embed-key': deployment.publishableKey,
							'api-url': endpoints.runtimeApiUrl,
							'view-id': viewId,
							input: JSON.stringify(input),
							style: { display: 'block', minHeight: 400 },
						})}
					</DialogContent>
				</Dialog>
			)}
		</Stack>
	);
}
